import {
  ButtonDatum,
  ButtonSelection,
  DragEvent as D3DragEvent,
} from "app/core/components/dashboard/d3/types"
import { SvgEventHandlers } from "./types"
import * as select from "app/core/components/dashboard/d3/select"
import * as svg from "app/core/components/dashboard/d3/svg"
import * as d3 from "d3"
import * as shared from "./shared"
import * as button from "app/core/components/dashboard/d3/button"
import * as snap from "app/core/components/dashboard/d3/snap"
import * as resizers from "app/core/components/dashboard/d3/resizers"
import * as screen from "app/core/components/dashboard/d3/screen"

export const register = (
  event: D3DragEvent,
  d3Element: ButtonSelection,
  handlers?: SvgEventHandlers,
) => {
  const draggedButtons = select.isSelected(d3Element) ? select.getSelection() : d3Element
  if (!draggedButtons) {
    return
  }
  const nonDraggedButtons = select.isSelected(d3Element)
    ? select.getNonSelection()
    : (svg
        .buttons()
        .filter<SVGGElement>(
          (datum: ButtonDatum) => datum.uuid !== d3Element.attr("uuid"),
        ) as d3.Selection<
        SVGGElement,
        ButtonDatum,
        HTMLElement | SVGSVGElement,
        any
      > as ButtonSelection)

  const snapInfo = snap.init(nonDraggedButtons)
  // note: deliberately NOT the bare class name "dragging" — that collides with a
  // global `.dragging { pointer-events: none }` rule from EditPlaylistBar.sass
  draggedButtons.classed("dashboard-button--dragging", true)
  const originalPosition = { x: event.x, y: event.y }
  let originalData = draggedButtons?.data()
  const minX = shared.minimum(originalData, (item) => item.x)
  const minY = shared.minimum(originalData, (item) => item.y)
  const maxX = shared.maximum(originalData, (item) => item.x + item.width)
  const maxY = shared.maximum(originalData, (item) => item.y + item.height)
  const originalGroupPosition = {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  }
  let calledDrag = false
  event
    .on("drag", (event: D3DragEvent) => {
      calledDrag = true
      const moveOffset = {
        x: event.x - originalPosition.x,
        y: event.y - originalPosition.y,
      }
      const unsnappedGroupPosition = {
        x: originalGroupPosition.x + moveOffset.x,
        y: originalGroupPosition.y + moveOffset.y,
        width: originalGroupPosition.width,
        height: originalGroupPosition.height,
      }

      const snappedGroupPosition = snap.update(snapInfo, unsnappedGroupPosition)

      const snappedPositionConstrainedToScreen = screen.constrainPosition(snappedGroupPosition)
      const constrainedOffset = {
        x: snappedPositionConstrainedToScreen.x - originalGroupPosition.x,
        y: snappedPositionConstrainedToScreen.y - originalGroupPosition.y,
      }

      draggedButtons.each(function (buttonDatum, buttonIndex) {
        const d3Element = d3.select(this)
        d3Element.datum({
          ...originalData[buttonIndex],
          x: originalData[buttonIndex].x + constrainedOffset.x,
          y: originalData[buttonIndex].y + constrainedOffset.y,
        })
        button.computeAttributes(d3Element)
      })
      handlers?.drag?.(d3Element, event)
    })
    .on("end", (event) => {
      snap.done(snapInfo)

      draggedButtons.classed("dashboard-button--dragging", false)
      draggedButtons.each(function () {
        const d3Element = d3.select(this)
        resizers.snapToGridAfterDrag(d3Element)
      })
      // prevent calling dragEnd code for mouseUp events
      if (calledDrag) {
        handlers?.move?.(draggedButtons, event)
      }
    })
}
